// Manages interactive handles for shape manipulation
// Creates, positions, hit-tests, and disposes handle meshes

import * as THREE from 'three'
import type { GardenScene } from '@/scene/GardenScene'
import type { Handle, EditableShape } from '@/types/editor'
import { CornerHandle, type CornerPosition } from './CornerHandle'
import { RotationHandle } from './RotationHandle'
import { VertexHandle } from './VertexHandle'
import { MidpointHandle } from './MidpointHandle'
import { DPM_SCALE } from '@/constants/coordinates'
import { debug } from '@/utils/debug'

/** Handle sphere radius in meters */
const HANDLE_RADIUS = 0.15

/** Midpoint handle sphere radius in meters (smaller than vertex handles) */
const MIDPOINT_RADIUS = 0.09

/** Y offset above the shape top for handle positioning */
const HANDLE_Y_OFFSET = 0.1

/** Distance of rotation handle from shape north edge (meters) */
const ROTATION_OFFSET = 0.4

export class HandleManager {
  private scene: GardenScene
  private handleGroup: THREE.Group
  private handles: Handle[] = []

  // Rotation handle stem line (connects shape center to rotation handle)
  private stemLine: THREE.Line | null = null

  // Shared geometry/material (cloned per handle for independent disposal)
  private handleGeometry: THREE.SphereGeometry
  private raycaster = new THREE.Raycaster()

  constructor(scene: GardenScene) {
    this.scene = scene

    this.handleGroup = new THREE.Group()
    this.handleGroup.name = 'tool-handles'
    scene.getScene().add(this.handleGroup)

    this.handleGeometry = new THREE.SphereGeometry(HANDLE_RADIUS, 16, 16)

    debug.log('HandleManager', 'Initialized')
  }

  /**
   * Create appropriate handles for a shape.
   * Creates 4 corner handles + 1 rotation handle.
   */
  createHandlesFor(shape: EditableShape, accentColor: number = 0x4ecca3): void {
    this.clearHandles()

    const kind = shape.getShapeKind()
    const resizable = shape.isResizable?.() !== false

    if (resizable) {
      switch (kind) {
        case 'rectangle':
        case 'ellipse':
          this.createCornerHandles(shape, accentColor)
          break
        case 'polygon':
          this.createVertexHandles(shape, accentColor)
          break
      }
    }

    // Add rotation handle for all shape types
    this.createRotationHandle(shape, accentColor)

    debug.log('HandleManager', `Created ${this.handles.length} handles for ${kind}`)
  }

  /**
   * Remove and dispose all handles.
   */
  clearHandles(): void {
    for (const handle of this.handles) {
      this.handleGroup.remove(handle.mesh)
      handle.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose()
          if (child.material instanceof THREE.Material) {
            child.material.dispose()
          }
        }
      })
    }
    this.handles = []

    // Dispose stem line
    if (this.stemLine) {
      this.handleGroup.remove(this.stemLine)
      this.stemLine.geometry.dispose()
      if (this.stemLine.material instanceof THREE.Material) {
        this.stemLine.material.dispose()
      }
      this.stemLine = null
    }
  }

  /**
   * Hit-test mouse position against handle meshes.
   * Returns the handle under the cursor, or null.
   */
  hitTest(mouse: THREE.Vector2, camera: THREE.Camera): Handle | null {
    if (this.handles.length === 0) return null

    this.raycaster.setFromCamera(mouse, camera)
    const meshes = this.handles.map(h => h.mesh)
    const intersects = this.raycaster.intersectObjects(meshes, true)

    if (intersects.length === 0) return null

    // Walk up to find the root handle mesh (in case we hit a child like the ring)
    let hitObject = intersects[0].object
    while (hitObject.parent && hitObject.parent !== this.handleGroup) {
      hitObject = hitObject.parent as THREE.Object3D
    }

    return this.handles.find(h => h.mesh === hitObject) ?? null
  }

  /**
   * Update handle positions to match current shape geometry.
   * Call after shape is moved/resized/rotated to keep handles in sync.
   */
  updatePositions(shape: EditableShape): void {
    const center = shape.getCenter()
    const size = shape.getSize()
    const rotation = shape.getRotation()
    const cx = center.x * DPM_SCALE
    const cz = center.y * DPM_SCALE
    const hw = (size.width / 2) * DPM_SCALE
    const hd = (size.height / 2) * DPM_SCALE
    const handleY = this.getHandleY(shape)

    // Rotation in radians (negative because Three.js Y-up rotation convention)
    const rad = -rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    // Helper: rotate offset (dx, dz) around center
    // Uses Three.js Y-rotation matrix projected onto XZ plane
    const rotatePoint = (dx: number, dz: number) => ({
      x: cx + dx * cos + dz * sin,
      z: cz - dx * sin + dz * cos,
    })

    // Corner positions (offsets from center, before rotation)
    const cornerOffsets: Record<CornerPosition, { dx: number; dz: number }> = {
      nw: { dx: -hw, dz: -hd },
      ne: { dx: +hw, dz: -hd },
      se: { dx: +hw, dz: +hd },
      sw: { dx: -hw, dz: +hd },
    }

    const points = shape.getPoints()

    for (const handle of this.handles) {
      if (handle instanceof CornerHandle) {
        const offset = cornerOffsets[handle.position]
        if (offset) {
          const pos = rotatePoint(offset.dx, offset.dz)
          handle.mesh.position.set(pos.x, handleY, pos.z)
        }
      } else if (handle instanceof VertexHandle) {
        if (points && handle.index < points.length) {
          const p = points[handle.index]
          const pos = rotatePoint(p.x * DPM_SCALE, p.y * DPM_SCALE)
          handle.mesh.position.set(pos.x, handleY, pos.z)
        }
      } else if (handle instanceof MidpointHandle) {
        if (points && handle.edgeStart < points.length && handle.edgeEnd < points.length) {
          const a = points[handle.edgeStart]
          const b = points[handle.edgeEnd]
          const mx = (a.x + b.x) / 2
          const my = (a.y + b.y) / 2
          const pos = rotatePoint(mx * DPM_SCALE, my * DPM_SCALE)
          handle.mesh.position.set(pos.x, handleY, pos.z)
        }
      } else if (handle instanceof RotationHandle) {
        const pos = rotatePoint(0, -hd - ROTATION_OFFSET)
        handle.mesh.position.set(pos.x, handleY, pos.z)
      }
    }

    // Update stem line
    this.updateStemLine(cx, cz, handleY, rotatePoint(0, -hd - ROTATION_OFFSET))
  }

  /**
   * Get all current handles.
   */
  getHandles(): Handle[] {
    return this.handles
  }

  /**
   * Dispose of all resources.
   */
  dispose(): void {
    this.clearHandles()
    this.handleGeometry.dispose()
    this.scene.getScene().remove(this.handleGroup)
    debug.log('HandleManager', 'Disposed')
  }

  // --- Private ---

  private createCornerHandles(shape: EditableShape, accentColor: number): void {
    const center = shape.getCenter()
    const size = shape.getSize()
    const rotation = shape.getRotation()

    const cx = center.x * DPM_SCALE
    const cz = center.y * DPM_SCALE
    const hw = (size.width / 2) * DPM_SCALE
    const hd = (size.height / 2) * DPM_SCALE
    const handleY = this.getHandleY(shape)

    const rad = -rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const rotatePoint = (dx: number, dz: number) => ({
      x: cx + dx * cos + dz * sin,
      z: cz - dx * sin + dz * cos,
    })

    const corners: { pos: CornerPosition; dx: number; dz: number }[] = [
      { pos: 'nw', dx: -hw, dz: -hd },
      { pos: 'ne', dx: +hw, dz: -hd },
      { pos: 'se', dx: +hw, dz: +hd },
      { pos: 'sw', dx: -hw, dz: +hd },
    ]

    for (const { pos, dx, dz } of corners) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.9,
      })

      const rotated = rotatePoint(dx, dz)
      const mesh = new THREE.Mesh(this.handleGeometry.clone(), material)
      mesh.position.set(rotated.x, handleY, rotated.z)

      // Colored ring around handle
      const ringGeometry = new THREE.RingGeometry(HANDLE_RADIUS * 0.8, HANDLE_RADIUS * 1.2, 16)
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: accentColor,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
      })
      const ring = new THREE.Mesh(ringGeometry, ringMaterial)
      ring.rotation.x = -Math.PI / 2
      ring.position.y = 0.01
      mesh.add(ring)

      this.handleGroup.add(mesh)

      const handle = new CornerHandle(pos, mesh, shape)
      this.handles.push(handle)
    }
  }

  private createVertexHandles(shape: EditableShape, accentColor: number): void {
    const points = shape.getPoints()
    const minPoints = shape.isOpenPath?.() ? 2 : 3
    if (!points || points.length < minPoints) return

    const center = shape.getCenter()
    const rotation = shape.getRotation()
    const cx = center.x * DPM_SCALE
    const cz = center.y * DPM_SCALE
    const handleY = this.getHandleY(shape)

    const rad = -rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    const toWorld = (px: number, py: number) => ({
      x: cx + px * DPM_SCALE * cos + py * DPM_SCALE * sin,
      z: cz - px * DPM_SCALE * sin + py * DPM_SCALE * cos,
    })

    // Vertex handles (white sphere + accent ring)
    for (let i = 0; i < points.length; i++) {
      const p = points[i]
      const world = toWorld(p.x, p.y)

      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.95,
      })
      const mesh = new THREE.Mesh(this.handleGeometry.clone(), material)
      mesh.position.set(world.x, handleY, world.z)

      // Accent ring for visibility
      const ringGeometry = new THREE.RingGeometry(HANDLE_RADIUS * 0.8, HANDLE_RADIUS * 1.3, 16)
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: accentColor,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide,
      })
      const ring = new THREE.Mesh(ringGeometry, ringMaterial)
      ring.rotation.x = -Math.PI / 2
      ring.position.y = 0.01
      mesh.add(ring)

      this.handleGroup.add(mesh)

      const handle = new VertexHandle(i, mesh, shape)
      this.handles.push(handle)
    }

    // Midpoint handles (smaller white spheres with accent ring on edge midpoints)
    const midpointGeometry = new THREE.SphereGeometry(MIDPOINT_RADIUS, 10, 10)
    const midpointRingGeometry = new THREE.RingGeometry(MIDPOINT_RADIUS * 0.7, MIDPOINT_RADIUS * 1.3, 12)
    const isOpen = shape.isOpenPath?.() ?? false
    const midpointCount = isOpen ? points.length - 1 : points.length
    for (let i = 0; i < midpointCount; i++) {
      const j = (i + 1) % points.length
      const a = points[i]
      const b = points[j]
      const mx = (a.x + b.x) / 2
      const my = (a.y + b.y) / 2
      const world = toWorld(mx, my)

      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.8,
      })
      const mesh = new THREE.Mesh(midpointGeometry.clone(), material)
      mesh.position.set(world.x, handleY, world.z)

      const ringMaterial = new THREE.MeshBasicMaterial({
        color: accentColor,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
      })
      const ring = new THREE.Mesh(midpointRingGeometry.clone(), ringMaterial)
      ring.rotation.x = -Math.PI / 2
      ring.position.y = 0.01
      mesh.add(ring)

      this.handleGroup.add(mesh)

      const handle = new MidpointHandle(i, j, mesh, shape)
      this.handles.push(handle)
    }
    midpointGeometry.dispose()
    midpointRingGeometry.dispose()
  }

  private createRotationHandle(shape: EditableShape, accentColor: number): void {
    const center = shape.getCenter()
    const size = shape.getSize()
    const rotation = shape.getRotation()

    const cx = center.x * DPM_SCALE
    const cz = center.y * DPM_SCALE
    const hd = (size.height / 2) * DPM_SCALE
    const handleY = this.getHandleY(shape)

    const rad = -rotation * Math.PI / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    // Position: north of shape, offset by ROTATION_OFFSET
    const dx = 0
    const dz = -hd - ROTATION_OFFSET
    const rotatedPos = {
      x: cx + dx * cos + dz * sin,
      z: cz - dx * sin + dz * cos,
    }

    // Rotation handle sphere — accent-colored (distinct from white corner handles)
    const material = new THREE.MeshBasicMaterial({
      color: accentColor,
      transparent: true,
      opacity: 0.9,
    })

    const mesh = new THREE.Mesh(this.handleGeometry.clone(), material)
    mesh.position.set(rotatedPos.x, handleY, rotatedPos.z)

    // White ring around rotation handle (inverted colors from corners)
    const ringGeometry = new THREE.RingGeometry(HANDLE_RADIUS * 0.8, HANDLE_RADIUS * 1.2, 16)
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    })
    const ring = new THREE.Mesh(ringGeometry, ringMaterial)
    ring.rotation.x = -Math.PI / 2
    ring.position.y = 0.01
    mesh.add(ring)

    this.handleGroup.add(mesh)

    const handle = new RotationHandle(mesh, shape)
    this.handles.push(handle)

    // Stem line from shape center to rotation handle
    this.createStemLine(cx, cz, handleY, rotatedPos, accentColor)
  }

  private createStemLine(
    cx: number, cz: number, y: number,
    handlePos: { x: number; z: number },
    color: number
  ): void {
    const points = [
      new THREE.Vector3(cx, y, cz),
      new THREE.Vector3(handlePos.x, y, handlePos.z),
    ]
    const geometry = new THREE.BufferGeometry().setFromPoints(points)
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0.5,
    })
    this.stemLine = new THREE.Line(geometry, material)
    this.handleGroup.add(this.stemLine)
  }

  private updateStemLine(cx: number, cz: number, y: number, handlePos: { x: number; z: number }): void {
    if (!this.stemLine) return

    const positions = this.stemLine.geometry.attributes.position as THREE.BufferAttribute
    positions.setXYZ(0, cx, y, cz)
    positions.setXYZ(1, handlePos.x, y, handlePos.z)
    positions.needsUpdate = true
  }

  private getHandleY(shape: EditableShape): number {
    void shape
    return HANDLE_Y_OFFSET
  }
}
